import React, { useState, useEffect } from 'react';
import { get, post, put } from '../../utils/apiClient';
import { clearCacheKey } from '../../utils/cacheUtils';
import {
  AdminSectionTitle,
  AdminSectionText,
  AdminLoginLabel,
  AdminLoginInput,
  AdminLoginButton,
  AdminButton,
  AdminSaveButton,
} from '../../styles/AdminPageStyles';

const AdminResourcesPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [uploadingSection, setUploadingSection] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [uploadTitles, setUploadTitles] = useState({});

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await get('/resources');
        setPageData(data);
      } catch (err) {
        setError(err && err.message ? err.message : 'Failed to load resources page.');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, []);

  const handleHeaderTitleChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      header: {
        ...((prev && prev.header) || {}),
        title: value,
      },
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!pageData) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const payload = {
        header: {
          ...(pageData.header || {}),
          title:
            (pageData.header && pageData.header.title) ||
            'Acts Fellowship Resources / Training',
        },
        teachingVideos: Array.isArray(pageData.teachingVideos)
          ? pageData.teachingVideos
          : [],
        otSessions: Array.isArray(pageData.otSessions)
          ? pageData.otSessions
          : [],
        ntSessions: Array.isArray(pageData.ntSessions)
          ? pageData.ntSessions
          : [],
        dbsSessions: Array.isArray(pageData.dbsSessions)
          ? pageData.dbsSessions
          : [],
        nurturingSessions: Array.isArray(pageData.nurturingSessions)
          ? pageData.nurturingSessions
          : [],
        audioLessons: Array.isArray(pageData.audioLessons)
          ? pageData.audioLessons
          : [],
      };

      const updated = await put('/resources', payload);
      setPageData(updated);
      clearCacheKey('resources_data');
      setSaveSuccess('Resources page saved successfully.');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to save resources header.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (sectionKey, file, title) => {
    if (!file) return;

    setUploadingSection(sectionKey);
    setUploadError(null);

    const pathMap = {
      teachingVideos: '/resources/teaching-videos/upload',
      otSessions: '/resources/ot-sessions/upload',
      ntSessions: '/resources/nt-sessions/upload',
      dbsSessions: '/resources/dbs-sessions/upload',
      nurturingSessions: '/resources/nurturing-sessions/upload',
      audioLessons: '/resources/audio-lessons/upload',
    };

    const path = pathMap[sectionKey];
    if (!path) {
      setUploadingSection(null);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (title && title.trim()) {
        formData.append('title', title.trim());
      }
      await post(path, formData);
      const data = await get('/resources');
      setPageData(data);
      clearCacheKey('resources_data');
      setUploadTitles((prev) => ({
        ...prev,
        [sectionKey]: '',
      }));
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload media file.');
    } finally {
      setUploadingSection(null);
    }
  };

  if (loading && !pageData) {
    return (
      <>
        <AdminSectionTitle>Resources / Training CMS</AdminSectionTitle>
        <AdminSectionText>Loading resources page...</AdminSectionText>
      </>
    );
  }

  const teachingVideos = pageData && Array.isArray(pageData.teachingVideos) ? pageData.teachingVideos : [];
  const otSessions = pageData && Array.isArray(pageData.otSessions) ? pageData.otSessions : [];
  const ntSessions = pageData && Array.isArray(pageData.ntSessions) ? pageData.ntSessions : [];
  const dbsSessions = pageData && Array.isArray(pageData.dbsSessions) ? pageData.dbsSessions : [];
  const nurturingSessions = pageData && Array.isArray(pageData.nurturingSessions) ? pageData.nurturingSessions : [];
  const audioLessons = pageData && Array.isArray(pageData.audioLessons) ? pageData.audioLessons : [];

  const renderSectionSummary = (label, items) => (
    <AdminSectionText style={{ fontSize: '0.9rem' }}>
      {label}: {items.length} item{items.length === 1 ? '' : 's'}
    </AdminSectionText>
  );

  const renderUploadControl = (sectionKey, label) => (
    <div style={{ marginBottom: 12 }}>
      <AdminLoginLabel>{label}</AdminLoginLabel>
      {sectionKey === 'audioLessons' && (
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor={`${sectionKey}-upload-title`}>
            Title (optional)
          </AdminLoginLabel>
          <AdminLoginInput
            id={`${sectionKey}-upload-title`}
            value={(uploadTitles && uploadTitles[sectionKey]) || ''}
            onChange={(e) =>
              setUploadTitles((prev) => ({
                ...prev,
                [sectionKey]: e.target.value,
              }))
            }
          />
        </div>
      )}
      <input
        type="file"
        onChange={(e) => {
          const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
          if (file) {
            const title =
              uploadTitles && uploadTitles[sectionKey]
                ? uploadTitles[sectionKey]
                : '';
            handleUpload(sectionKey, file, title);
          }
        }}
        style={{ fontSize: '0.9rem' }}
      />
      {uploadingSection === sectionKey && (
        <AdminSectionText style={{ fontSize: '0.85rem' }}>
          Uploading...
        </AdminSectionText>
      )}
    </div>
  );

  const updateMediaItem = (sectionKey, index, field, value) => {
    setPageData((prev) => {
      if (!prev || !Array.isArray(prev[sectionKey])) return prev;
      const list = prev[sectionKey].map((item, i) => (
        i === index
          ? {
              ...item,
              [field]: value,
            }
          : item
      ));
      return {
        ...prev,
        [sectionKey]: list,
      };
    });
  };

  const addLinkItem = (sectionKey) => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const list = Array.isArray(next[sectionKey]) ? [...next[sectionKey]] : [];
      list.push({
        title: '',
        filename: '',
        url: '',
      });
      next[sectionKey] = list;
      return next;
    });
  };

  const removeMediaItem = async (sectionKey, index) => {
    if (!pageData || !Array.isArray(pageData[sectionKey])) return;

    const ok = window.confirm('Remove this item from the section?');
    if (!ok) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const nextItems = pageData[sectionKey].filter((_, i) => i !== index);

      const keys = [
        'teachingVideos',
        'otSessions',
        'ntSessions',
        'dbsSessions',
        'nurturingSessions',
        'audioLessons',
      ];

      const payload = {
        header: {
          ...(pageData.header || {}),
          title:
            (pageData.header && pageData.header.title) ||
            'Acts Fellowship Resources / Training',
        },
      };

      keys.forEach((key) => {
        const source = key === sectionKey
          ? nextItems
          : (Array.isArray(pageData[key]) ? pageData[key] : []);
        payload[key] = source;
      });

      const updated = await put('/resources', payload);
      setPageData(updated);
      clearCacheKey('resources_data');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to remove media item.');
    } finally {
      setSaving(false);
    }
  };

  const renderMediaListEditor = (sectionKey, items) => {
    if (!items || items.length === 0) {
      return (
        <AdminSectionText style={{ fontSize: '0.85rem' }}>
          No items in this section yet.
        </AdminSectionText>
      );
    }

    return (
      <div style={{ marginTop: 8 }}>
        {items.map((item, index) => (
          <div
            key={item._id || item.filename || index}
            style={{
              border: '1px solid #ddd',
              borderRadius: 8,
              padding: 10,
              marginBottom: 8,
              fontSize: '0.9rem',
            }}
          >
            <div style={{ marginBottom: 6 }}>
              <AdminLoginLabel htmlFor={`${sectionKey}-title-${index}`}>
                Title
              </AdminLoginLabel>
              <AdminLoginInput
                id={`${sectionKey}-title-${index}`}
                value={item.title || ''}
                onChange={(e) => updateMediaItem(sectionKey, index, 'title', e.target.value)}
              />
            </div>
            <div style={{ marginBottom: 6 }}>
              <AdminLoginLabel htmlFor={`${sectionKey}-filename-${index}`}>
                Original Filename / Label (optional)
              </AdminLoginLabel>
              <AdminLoginInput
                id={`${sectionKey}-filename-${index}`}
                value={item.filename || ''}
                onChange={(e) => updateMediaItem(sectionKey, index, 'filename', e.target.value)}
              />
            </div>
            <div style={{ marginBottom: 6 }}>
              <AdminLoginLabel htmlFor={`${sectionKey}-url-${index}`}>
                Link URL (optional)
              </AdminLoginLabel>
              <AdminLoginInput
                id={`${sectionKey}-url-${index}`}
                value={item.url || ''}
                onChange={(e) => updateMediaItem(sectionKey, index, 'url', e.target.value)}
              />
            </div>
            <div style={{ fontSize: '0.8rem', marginBottom: 6 }}>
              {item.contentType && <span>{item.contentType}</span>}
            </div>
            <AdminButton type="button" onClick={() => removeMediaItem(sectionKey, index)}>
              Remove Item
            </AdminButton>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <AdminSectionTitle>Resources / Training CMS</AdminSectionTitle>

      <form onSubmit={handleSave} style={{ marginBottom: 24 }}>
        <AdminSectionText style={{ marginBottom: 8 }}>
          Update the page header title.
        </AdminSectionText>
        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="resources-title">Header Title</AdminLoginLabel>
          <AdminLoginInput
            id="resources-title"
            value={(pageData && pageData.header && pageData.header.title) || ''}
            onChange={handleHeaderTitleChange}
          />
        </div>

      <hr style={{ margin: '16px 0' }} />

      <AdminSectionTitle>Teaching Videos</AdminSectionTitle>
      {renderSectionSummary('Total teaching videos', teachingVideos)}
      {renderUploadControl('teachingVideos', 'Upload teaching video')}
      {renderMediaListEditor('teachingVideos', teachingVideos)}
      <div style={{ marginTop: 8 }}>
        <AdminButton type="button" onClick={() => addLinkItem('teachingVideos')}>
          Add link item
        </AdminButton>
      </div>

      <hr style={{ margin: '16px 0' }} />

      <AdminSectionTitle>Old Testament Sessions</AdminSectionTitle>
      {renderSectionSummary('Total OT sessions', otSessions)}
      {renderUploadControl('otSessions', 'Upload OT session')}
      {renderMediaListEditor('otSessions', otSessions)}
      <div style={{ marginTop: 8 }}>
        <AdminButton type="button" onClick={() => addLinkItem('otSessions')}>
          Add link item
        </AdminButton>
      </div>

      <hr style={{ margin: '16px 0' }} />

      <AdminSectionTitle>New Testament Sessions</AdminSectionTitle>
      {renderSectionSummary('Total NT sessions', ntSessions)}
      {renderUploadControl('ntSessions', 'Upload NT session')}
      {renderMediaListEditor('ntSessions', ntSessions)}
      <div style={{ marginTop: 8 }}>
        <AdminButton type="button" onClick={() => addLinkItem('ntSessions')}>
          Add link item
        </AdminButton>
      </div>

      <hr style={{ margin: '16px 0' }} />

      <AdminSectionTitle>DBS Sessions</AdminSectionTitle>
      {renderSectionSummary('Total DBS sessions', dbsSessions)}
      {renderUploadControl('dbsSessions', 'Upload DBS session')}
      {renderMediaListEditor('dbsSessions', dbsSessions)}
      <div style={{ marginTop: 8 }}>
        <AdminButton type="button" onClick={() => addLinkItem('dbsSessions')}>
          Add link item
        </AdminButton>
      </div>

      <hr style={{ margin: '16px 0' }} />

      <AdminSectionTitle>Nurturing Sessions</AdminSectionTitle>
      {renderSectionSummary('Total nurturing sessions', nurturingSessions)}
      {renderUploadControl('nurturingSessions', 'Upload nurturing session')}
      {renderMediaListEditor('nurturingSessions', nurturingSessions)}
      <div style={{ marginTop: 8 }}>
        <AdminButton type="button" onClick={() => addLinkItem('nurturingSessions')}>
          Add link item
        </AdminButton>
      </div>

      <hr style={{ margin: '16px 0' }} />

      <AdminSectionTitle>Audio Lessons</AdminSectionTitle>
      {renderSectionSummary('Total audio lessons', audioLessons)}
      {renderUploadControl('audioLessons', 'Upload audio lesson')}
      {renderMediaListEditor('audioLessons', audioLessons)}
      <div style={{ marginTop: 8 }}>
        <AdminButton type="button" onClick={() => addLinkItem('audioLessons')}>
          Add link item
        </AdminButton>
      </div>

      <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: '16px' }}>
        <AdminSaveButton type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </AdminSaveButton>
        {saveSuccess && (
          <span style={{ color: '#1b5e20', fontWeight: '500' }}>{saveSuccess}</span>
        )}
        {error && (
          <span style={{ color: '#b71c1c', fontWeight: '500' }}>{error}</span>
        )}
        {uploadError && (
          <span style={{ color: '#b71c1c', fontWeight: '500' }}>{uploadError}</span>
        )}
      </div>
    </form>
    </>
  );
};

export default AdminResourcesPage;
